import { useEffect } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../mantine/notifications/useToast";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import type { ConfigurationData } from "../ThirdPartySystems.types";
import {
  FIELDS_FOR_SYSTEM,
  NEW_CONFIGURATION_DEFAULT_VALUES,
  SUB_METERING_FORM_FIELD_DATA
} from "./SubMeteringConfigurationForm.constants";

export const SUB_METERING_FORM_ID = "sub-metering-configuration-form";

export enum SubmeteringConfigurationFormMode {
  New = "new",
  Edit = "edit"
}

interface SubMeteringConfigurationFormProps {
  initialConfigurationData?: ConfigurationData;
  mode?: SubmeteringConfigurationFormMode;
  variantId: number;
  onSubmit: (data: ConfigurationData) => Promise<ConfigurationData>;
}

function SubMeteringConfigurationForm({
  initialConfigurationData,
  mode = SubmeteringConfigurationFormMode.New,
  onSubmit
}: SubMeteringConfigurationFormProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
    watch
  } = useForm<ConfigurationData>({
    defaultValues: initialConfigurationData ?? NEW_CONFIGURATION_DEFAULT_VALUES,
    mode: "onBlur"
  });

  const selectedSystem = watch("subMeteringSystem");
  const fieldsForSelectedSystem = FIELDS_FOR_SYSTEM[selectedSystem] ?? [];
  const isEditMode = mode === SubmeteringConfigurationFormMode.Edit;
  const subMeteringSystemFieldData = {
    ...SUB_METERING_FORM_FIELD_DATA.subMeteringSystem,
    disabled: isEditMode
  };

  useEffect(() => {
    const fieldsForCurrentSystem = FIELDS_FOR_SYSTEM[selectedSystem] ?? [];

    if (!fieldsForCurrentSystem.includes("url")) {
      setValue("url", null);
    }
    if (!fieldsForCurrentSystem.includes("username")) {
      setValue("username", "");
    }
    if (!fieldsForCurrentSystem.includes("password")) {
      setValue("password", "");
    }
    if (!fieldsForCurrentSystem.includes("token")) {
      setValue("token", null);
    }
    if (!fieldsForCurrentSystem.includes("clientId")) {
      setValue("clientId", null);
    }
  }, [selectedSystem, setValue]);

  const onValidSubmit: SubmitHandler<ConfigurationData> = async (data) => {
    try {
      return await onSubmit(data);
    } catch (error) {
      setErrorsFromResponseData<ConfigurationData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  return (
    <div className="SubMeteringConfigurationForm">
      <form id={SUB_METERING_FORM_ID} onSubmit={submitCallback}>
        <FormFieldController
          control={control}
          data={subMeteringSystemFieldData}
          error={errors.subMeteringSystem}
        />

        <FormFieldController
          control={control}
          data={SUB_METERING_FORM_FIELD_DATA.name}
          error={errors.name}
        />

        {fieldsForSelectedSystem.includes("url") && (
          <FormFieldController
            control={control}
            data={SUB_METERING_FORM_FIELD_DATA.url}
            error={errors.url}
            rules={{
              validate: (value) => {
                if (!value) return true;

                try {
                  const url = new URL(value);
                  if (!["http:", "https:"].includes(url.protocol)) {
                    return "URL muss mit http:// oder https:// beginnen";
                  }
                  const isIPAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(
                    url.hostname
                  );
                  if (isIPAddress) {
                    return "URL darf keine IP-Adresse sein, bitte einen Domainnamen verwenden";
                  }
                  return true;
                } catch {
                  return "Ungültige URL";
                }
              }
            }}
          />
        )}

        {fieldsForSelectedSystem.includes("clientId") && (
          <FormFieldController
            control={control}
            data={SUB_METERING_FORM_FIELD_DATA.clientId}
            error={errors.clientId}
          />
        )}

        {fieldsForSelectedSystem.includes("username") && (
          <FormFieldController
            control={control}
            data={SUB_METERING_FORM_FIELD_DATA.username}
            error={errors.username}
          />
        )}

        {fieldsForSelectedSystem.includes("password") && (
          <FormFieldController
            control={control}
            data={SUB_METERING_FORM_FIELD_DATA.password}
            error={errors.password}
          />
        )}

        {fieldsForSelectedSystem.includes("token") && (
          <FormFieldController
            control={control}
            data={SUB_METERING_FORM_FIELD_DATA.token}
            error={errors.token}
          />
        )}
      </form>
    </div>
  );
}

export { SubMeteringConfigurationForm, SubMeteringConfigurationFormProps };
